import { LinearProgress, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Box, BoxProps } from "@mui/system";
import React from "react";
import { DropzoneOptions, useDropzone } from "react-dropzone";
import { Choose, classNames } from "react-extras";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: theme.spacing(0.25),
    borderRadius: theme.spacing(0.25),
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
    height: "100%",
    justifyContent: "center",
  },
  active: {
    borderColor: theme.palette.secondary.main,
  },
}));

interface FileUploadProps {
  infoText?: string;
  uploadProgress?: number | undefined;
  options?: DropzoneOptions;
}

type Props = FileUploadProps;

const FileUpload = ({
  infoText,
  uploadProgress,
  options,
  ...rest
}: Props &
  Omit<
    Partial<BoxProps<"div", { component: "div" }>>,
    "onDrop"
  >): JSX.Element => {
  const classes = useStyles();
  const { t } = useTranslation("file-explorer");
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...options,
    // Maximum file size (in bytes)
    maxSize: options?.maxSize ?? 2000 * 1024 * 1024,
  });

  return (
    <Box
      component="div"
      className={classNames({
        [classes.root]: true,
        [classes.active]: isDragActive,
      })}
      {...getRootProps()}
      {...rest}
    >
      <Choose>
        <Choose.When condition={!!uploadProgress && uploadProgress !== 100}>
          <Typography variant="body1">
            {t("uploading-progress", { percent: uploadProgress ?? 100 })}
          </Typography>
          <LinearProgress
            style={{ width: "100%", marginTop: "8px", height: "8px" }}
            color="secondary"
            variant="determinate"
            value={uploadProgress}
          />
        </Choose.When>
        <Choose.Otherwise>
          <input {...getInputProps()} />
          {isDragActive ? (
            <Typography variant="body1">{t("drop-files")}</Typography>
          ) : (
            <Typography variant="body1">{t("drop-files-or-click")}</Typography>
          )}
          <Typography variant="body2">
            {infoText ?? t("size-and-formats")}
          </Typography>
        </Choose.Otherwise>
      </Choose>
    </Box>
  );
};

export default FileUpload;
